import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Instagram, Facebook, Download, Copy, Check, Share2, Heart, Sparkles, BookOpen } from "lucide-react";
import axios from "axios";
import { fetchRestaurant, hasToken } from "../lib/api";
import { RestaurantHeader } from "../components/RestaurantHeader";
import { SubPageNav } from "../components/SubPageNav";
import { Toaster } from "../components/ui/toaster";
import { toast } from "../hooks/use-toast";
import { tr } from "../lib/i18n";
import AppBottomBar from "../components/AppBottomBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IG_URL = "https://www.instagram.com/ilpozzodeigiganti/";
const FB_URL = "https://www.facebook.com/profile.php?id=61572083007558";
const IG_HANDLE = "@ilpozzodeigiganti";
const FB_NAME = "Il Pozzo dei Giganti";

// Official hashtag bank (copy-paste ready per post type)
const HASHTAG_BANK = {
  brand: ["#IlPozzoDeiGiganti", "#PozzoDeiGiganti", "#AgriturismoMarche", "#MonteSanPietrangeli"],
  territory: ["#Marche", "#MarcheDaScoprire", "#Fermano", "#Sibillini", "#Piceno", "#BorghiMarche"],
  food: ["#CucinaMarchigiana", "#OlivaAscolana", "#Ciauscolo", "#Vincisgrassi", "#KmZero", "#SlowFood"],
  spa: ["#SpaMarche", "#WellnessRetreat", "#RelaxAgriturismo", "#WeekendSpa", "#Piscina"],
  family: ["#WeekendInFamiglia", "#AgriturismoFamily", "#GitaFuoriPorta"],
  eu: ["#ItalyTravel", "#SlowTravelItaly", "#HiddenItaly", "#AuthenticItaly"],
};

const S = {
  title:     { it: "Seguici sui social",       en: "Follow us on social",       es: "Síguenos en redes",       fr: "Suivez-nous",              de: "Folge uns" },
  subtitle:  { it: "Un posto, mille racconti. Ogni settimana.", en: "One place, a thousand stories. Every week.", es: "Un lugar, mil historias. Cada semana.", fr: "Un lieu, mille histoires. Chaque semaine.", de: "Ein Ort, tausend Geschichten. Jede Woche." },
  scanQR:    { it: "Inquadra per aprire il profilo", en: "Scan to open the profile", es: "Escanea para abrir el perfil", fr: "Scannez pour ouvrir le profil", de: "Scannen zum Öffnen" },
  openProfile: { it: "Apri il profilo",       en: "Open profile",              es: "Abrir perfil",            fr: "Ouvrir le profil",         de: "Profil öffnen" },
  downloadQR:  { it: "Scarica QR",             en: "Download QR",               es: "Descargar QR",            fr: "Télécharger le QR",        de: "QR herunterladen" },
  hashtags:  { it: "Hashtag ufficiali",       en: "Official hashtags",         es: "Hashtags oficiales",      fr: "Hashtags officiels",       de: "Offizielle Hashtags" },
  hashtagHint: { it: "Copia e usa quando pubblichi contenuti che ci taggano.", en: "Copy and use when posting content that tags us.", es: "Copia y usa al publicar contenido que nos etiquete.", fr: "Copiez et utilisez pour vos publications qui nous taguent.", de: "Kopiere und nutze für Beiträge, die uns markieren." },
  copied:    { it: "Copiato!",                 en: "Copied!",                    es: "¡Copiado!",               fr: "Copié !",                  de: "Kopiert!" },
  copy:      { it: "Copia",                    en: "Copy",                       es: "Copiar",                  fr: "Copier",                   de: "Kopieren" },
  taggaci:   { it: "Taggaci nelle tue foto",   en: "Tag us in your photos",     es: "Etiquétanos en tus fotos",fr: "Taguez-nous dans vos photos", de: "Markiere uns in deinen Fotos" },
  taggaciHint: { it: "Ripubblichiamo le più belle nelle nostre stories 📸", en: "We'll repost the best in our stories 📸", es: "Republicaremos las mejores en nuestras stories 📸", fr: "Nous republions les plus belles dans nos stories 📸", de: "Wir teilen die schönsten in unseren Stories 📸" },
  brandStory:{ it: "La nostra storia",         en: "Our story",                 es: "Nuestra historia",        fr: "Notre histoire",           de: "Unsere Geschichte" },
  brandStoryText: {
    it: "Il Pozzo dei Giganti nasce nel cuore del Fermano, tra ulivi e ciliegi. \"I Giganti\" sono Nonno Franco e suo fratello Adino: con le loro mani i due fratelli hanno costruito questa casa e curato questa terra tutta la vita. Al loro fianco, la figura di Nonna Angela — amata profondamente da Elisa — che ha riempito queste stanze di ricette, cura e affetti. Oggi Elisa custodisce tutto con la stessa dedizione, offrendo cucina marchigiana autentica, spa panoramica, camere pensate per riposare davvero. A 15 minuti dal mare, a un passo dai Sibillini.",
    en: "Il Pozzo dei Giganti was born in the heart of the Fermo hills. \"The Giants\" are Nonno Franco and his brother Adino: with their hands the two brothers built this house and tended this land their whole lives. Beside them, the figure of Nonna Angela — deeply loved by Elisa — filled these rooms with recipes, care and warmth. Today Elisa keeps it all alive with the same dedication: authentic Marchigian cuisine, a panoramic spa, rooms designed for real rest. 15 minutes from the sea, a step from the Sibillini mountains.",
    es: "Il Pozzo dei Giganti nació en el corazón del Fermano. \"Los Gigantes\" son Nonno Franco y su hermano Adino: con sus manos, los dos hermanos construyeron esta casa y cuidaron esta tierra toda la vida. Junto a ellos, la figura de Nonna Angela — profundamente amada por Elisa — llenó estas estancias de recetas, cuidados y afecto. Hoy Elisa lo custodia todo con la misma dedicación: cocina marchigiana auténtica, spa panorámica, habitaciones para descansar de verdad.",
    fr: "Il Pozzo dei Giganti est né au cœur du Fermano. « Les Géants » sont Nonno Franco et son frère Adino : de leurs mains, les deux frères ont bâti cette maison et cultivé cette terre toute leur vie. À leurs côtés, la figure de Nonna Angela — profondément aimée d'Elisa — a rempli ces pièces de recettes, de soin et de tendresse. Aujourd'hui Elisa en prend soin avec la même dévotion : cuisine authentique des Marches, spa panoramique, chambres pour un vrai repos.",
    de: "Il Pozzo dei Giganti entstand im Herzen des Fermano. „Die Giganten\" sind Nonno Franco und sein Bruder Adino: Mit ihren Händen haben die beiden Brüder dieses Haus erbaut und dieses Land ein Leben lang bestellt. An ihrer Seite hat Nonna Angela — von Elisa tief geliebt — diese Räume mit Rezepten, Fürsorge und Wärme erfüllt. Heute pflegt Elisa alles mit derselben Hingabe: authentische Marken-Küche, Panorama-Spa, Zimmer für echte Erholung.",
  },
  share:     { it: "Condividi la pagina",      en: "Share this page",           es: "Compartir esta página",   fr: "Partager cette page",      de: "Diese Seite teilen" },
};

// QR generator (same service used by the existing QRDialog)
const qrFor = (url, size = 400, color = "1c1917") =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&color=${color}&data=${encodeURIComponent(url)}`;

// Fire-and-forget tracking. Silently fails.
const track = (type, extra = {}) => {
  try {
    axios.post(`${API}/track`, { type, ...extra });
  } catch (_) { /* ignore */ }
};

export default function SocialPage() {
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);
  useEffect(() => { fetchRestaurant().then(setRestaurant).catch(() => {}); }, []);
  useEffect(() => { track("social_view", { lang, source: new URLSearchParams(window.location.search).get("src") }); }, []); // eslint-disable-line

  const openIG = () => { track("social_click_ig"); window.open(IG_URL, "_blank", "noopener"); };
  const openFB = () => { track("social_click_fb"); window.open(FB_URL, "_blank", "noopener"); };

  const downloadQR = async (url, filename) => {
    try {
      const res = await fetch(qrFor(url, 800, "4a1c17"));
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (_) {
      window.open(qrFor(url, 800, "4a1c17"), "_blank");
    }
  };

  const copyHashtags = (key, list) => {
    const text = list.join(" ");
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      toast({ title: tr(S.copied, lang), description: text.slice(0, 80) + (text.length > 80 ? "…" : "") });
      setTimeout(() => setCopiedKey(null), 1800);
    }).catch(() => {});
  };

  const sharePage = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: FB_NAME, url }); } catch (_) {}
    } else {
      navigator.clipboard.writeText(url).then(() =>
        toast({ title: tr(S.copied, lang), description: url })
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#f7ebdf]">
      <RestaurantHeader restaurant={restaurant} lang={lang} setLang={setLang} compact stickyBar />
      <main className="max-w-5xl mx-auto px-4 py-6" data-testid="social-page">
        <SubPageNav lang={lang} />

        {/* Hero */}
        <section className="mt-2 mb-6 text-center sm:text-left">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/60 border border-[#e8dcc8] px-3 py-1 text-xs font-semibold text-[#7c5a4e] uppercase tracking-wide">
            <Sparkles className="h-3.5 w-3.5 text-[#c48b31]" />
            Social · #IlPozzoDeiGiganti
          </div>
          <h1 className="mt-3 font-serif text-3xl sm:text-5xl font-bold text-[#2b1410] leading-tight">
            {tr(S.title, lang)}
          </h1>
          <p className="mt-2 text-base sm:text-lg text-[#7c5a4e] max-w-2xl">
            {tr(S.subtitle, lang)}
          </p>
        </section>

        {/* Piano editoriale rimosso — ora vive solo nell'admin (tab Social) */}

        {/* Social cards */}
        <section className="grid sm:grid-cols-2 gap-4 mb-8" data-testid="social-cards">
          {/* Instagram card */}
          <div className="rounded-2xl bg-white border border-[#e8dcc8] shadow-sm overflow-hidden">
            <div className="p-5 bg-gradient-to-br from-[#4a1c17] via-[#7c2d20] to-[#c48b31] text-[#f7ebdf]">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-white/15 border border-white/25 flex items-center justify-center backdrop-blur-sm">
                  <Instagram className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-80">Instagram</div>
                  <div className="font-serif text-xl font-semibold">{IG_HANDLE}</div>
                </div>
              </div>
            </div>
            <div className="p-5 flex flex-col items-center">
              <div className="rounded-2xl bg-white ring-1 ring-[#e8dcc8] p-3">
                <img
                  src={qrFor(IG_URL, 400, "4a1c17")}
                  alt="QR Instagram"
                  className="h-48 w-48"
                  data-testid="social-qr-ig"
                />
              </div>
              <p className="mt-3 text-xs text-[#7c5a4e] text-center">{tr(S.scanQR, lang)}</p>
              <div className="mt-4 grid grid-cols-2 gap-2 w-full">
                <button
                  onClick={openIG}
                  data-testid="social-open-ig"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-2 text-sm font-medium transition-colors"
                >
                  <Instagram className="h-4 w-4" /> {tr(S.openProfile, lang)}
                </button>
                <button
                  onClick={() => downloadQR(IG_URL, "qr-instagram-ilpozzodeigiganti.png")}
                  data-testid="social-download-qr-ig"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee] px-3 py-2 text-sm font-medium transition-colors"
                >
                  <Download className="h-4 w-4" /> {tr(S.downloadQR, lang)}
                </button>
              </div>
            </div>
          </div>

          {/* Facebook card */}
          <div className="rounded-2xl bg-white border border-[#e8dcc8] shadow-sm overflow-hidden">
            <div className="p-5 bg-gradient-to-br from-[#1c3d7c] via-[#2b5eb0] to-[#4a1c17] text-[#f7ebdf]">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-white/15 border border-white/25 flex items-center justify-center backdrop-blur-sm">
                  <Facebook className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-80">Facebook</div>
                  <div className="font-serif text-xl font-semibold">{FB_NAME}</div>
                </div>
              </div>
            </div>
            <div className="p-5 flex flex-col items-center">
              <div className="rounded-2xl bg-white ring-1 ring-[#e8dcc8] p-3">
                <img
                  src={qrFor(FB_URL, 400, "4a1c17")}
                  alt="QR Facebook"
                  className="h-48 w-48"
                  data-testid="social-qr-fb"
                />
              </div>
              <p className="mt-3 text-xs text-[#7c5a4e] text-center">{tr(S.scanQR, lang)}</p>
              <div className="mt-4 grid grid-cols-2 gap-2 w-full">
                <button
                  onClick={openFB}
                  data-testid="social-open-fb"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-2 text-sm font-medium transition-colors"
                >
                  <Facebook className="h-4 w-4" /> {tr(S.openProfile, lang)}
                </button>
                <button
                  onClick={() => downloadQR(FB_URL, "qr-facebook-ilpozzodeigiganti.png")}
                  data-testid="social-download-qr-fb"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee] px-3 py-2 text-sm font-medium transition-colors"
                >
                  <Download className="h-4 w-4" /> {tr(S.downloadQR, lang)}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Tag us CTA */}
        <section className="mb-8 rounded-2xl bg-gradient-to-br from-[#4a1c17] to-[#2b1410] text-[#f7ebdf] p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-6 -right-6 opacity-10">
            <Heart className="h-40 w-40" />
          </div>
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-[#c48b31] font-semibold">
              {tr(S.taggaci, lang)}
            </div>
            <h2 className="mt-2 font-serif text-2xl sm:text-3xl font-bold">
              {IG_HANDLE}
            </h2>
            <p className="mt-2 text-sm sm:text-base opacity-90 max-w-xl">
              {tr(S.taggaciHint, lang)}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold">
                #IlPozzoDeiGiganti
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold">
                #PozzoDeiGiganti
              </span>
            </div>
          </div>
        </section>

        {/* Hashtag bank */}
        <section className="mb-8" data-testid="social-hashtags">
          <div className="flex items-end justify-between gap-3 mb-3">
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2b1410]">
                {tr(S.hashtags, lang)}
              </h2>
              <p className="text-sm text-[#7c5a4e] mt-1">{tr(S.hashtagHint, lang)}</p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.entries(HASHTAG_BANK).map(([key, list]) => {
              const labelMap = {
                brand:     { it: "Brand (sempre)",         en: "Brand (always)",       es: "Marca (siempre)",       fr: "Marque (toujours)",    de: "Marke (immer)" },
                territory: { it: "Territorio Marche",      en: "Marche territory",     es: "Territorio Marche",     fr: "Territoire Marche",    de: "Region Marken" },
                food:      { it: "Cucina & prodotti",      en: "Food & products",      es: "Cocina & productos",    fr: "Cuisine & produits",   de: "Küche & Produkte" },
                spa:       { it: "Spa & benessere",         en: "Spa & wellness",       es: "Spa & bienestar",       fr: "Spa & bien-être",       de: "Spa & Wellness" },
                family:    { it: "Famiglie",                en: "Family",               es: "Familias",              fr: "Familles",              de: "Familien" },
                eu:        { it: "Turisti EU / EN",         en: "EU / EN tourists",     es: "Turistas EU / EN",      fr: "Touristes EU / EN",     de: "EU-Touristen" },
              };
              const label = tr(labelMap[key] || { it: key }, lang);
              const isCopied = copiedKey === key;
              return (
                <div key={key} className="rounded-xl bg-white border border-[#e8dcc8] shadow-sm p-4" data-testid={`hashtag-group-${key}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-[#c48b31]">{label}</div>
                    <button
                      onClick={() => copyHashtags(key, list)}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold transition-colors ${
                        isCopied
                          ? "bg-emerald-100 border-emerald-200 text-emerald-800"
                          : "bg-[#faf3ee] border-[#e8dcc8] text-[#4a1c17] hover:bg-[#f7ebdf]"
                      }`}
                      data-testid={`hashtag-copy-${key}`}
                    >
                      {isCopied ? <><Check className="h-3 w-3" /> {tr(S.copied, lang)}</> : <><Copy className="h-3 w-3" /> {tr(S.copy, lang)}</>}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {list.map((h) => (
                      <span key={h} className="inline-block rounded-full bg-[#faf3ee] border border-[#e8dcc8] text-[#4a1c17] px-2 py-0.5 text-xs font-medium">
                        {h}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Brand story */}
        <section className="mb-10 rounded-2xl bg-white border border-[#e8dcc8] shadow-sm p-6 sm:p-8" data-testid="social-brand-story">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#c48b31] mb-2">
            {tr(S.brandStory, lang)}
          </div>
          <p className="font-serif text-base sm:text-lg text-[#2b1410] leading-relaxed">
            {tr(S.brandStoryText, lang)}
          </p>
          <div className="mt-5">
            <button
              onClick={sharePage}
              className="inline-flex items-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2 text-sm font-medium transition-colors"
              data-testid="social-share-page"
            >
              <Share2 className="h-4 w-4" /> {tr(S.share, lang)}
            </button>
          </div>
        </section>
      </main>
      <AppBottomBar current="none" lang={lang} />
      <Toaster />
    </div>
  );
}
